import { ENABLE_DESKTOP_FONT_SCALING } from '@/core/constants/appConfig';
import { logger } from '@/core/logger';
import { Platform } from 'react-native';

type Locale = 'en' | 'ar';

const ENABLE_WEB_SCALING_CONTENTFUL_KEY = 'enableWebScaling';

/** `null` = not loaded yet; only `true` enables scaling. */
let contentfulEnabled: boolean | null = null;
const listeners = new Set<() => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore subscriber errors
    }
  });
}

function parseEnableWebScalingFromLocalesRaw(raw: unknown): boolean {
  const items: unknown[] = Array.isArray((raw as { items?: unknown[] })?.items)
    ? ((raw as { items: unknown[] }).items ?? [])
    : [];

  for (const item of items) {
    const fields = (item as { fields?: Record<string, unknown> })?.fields ?? {};
    const key =
      typeof fields.key === 'string' ? fields.key.trim() : '';
    const value =
      typeof fields.value === 'string'
        ? fields.value.trim().toLowerCase()
        : '';

    if (key === ENABLE_WEB_SCALING_CONTENTFUL_KEY) {
      return value === 'true';
    }
  }

  return false;
}

/**
 * Whether web viewport scaling is active.
 * Requires app flag + Contentful `enableWebScaling` === `"true"`.
 */
export function isWebScalingEnabled(): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }
  if (!ENABLE_DESKTOP_FONT_SCALING) {
    return false;
  }
  return contentfulEnabled === true;
}

export function subscribeWebScalingConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Read `enableWebScaling` from the same locales API response as translations.
 * Call after `fetchAllNamespaces` (no extra Contentful request).
 */
export function applyWebScalingFromLocalesRaw(
  raw: unknown,
  locale?: Locale,
): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }

  const enabled = parseEnableWebScalingFromLocalesRaw(raw);
  contentfulEnabled = enabled;

  logger.info('webScalingConfigService', 'Applied from locales payload', {
    locale,
    enableWebScaling: enabled,
  });

  notifyListeners();
  return enabled;
}

/** Restore flag from persisted translation cache (offline / TTL hit). */
export function restoreWebScalingFromCache(enabled: boolean | undefined): void {
  if (Platform.OS !== 'web') {
    return;
  }

  contentfulEnabled = enabled === true;
  notifyListeners();
}
